import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './SharedNavbar.css';
import logo from '../assets/logos/dama.jpeg';

const SharedNavbar = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const navLinksRef = useRef(null);
  const isActive = (path) => location.pathname === path;

  /* ── Auto-scroll vers lien actif sur mobile ── */
  useEffect(() => {
    if (navLinksRef.current) {
      const activeLink = navLinksRef.current.querySelector('a.active');
      if (activeLink) {
        activeLink.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    const user = localStorage.getItem('dama_current_user');
    if (user) setCurrentUser(JSON.parse(user));
    else setCurrentUser(null);
  }, [location]);

  /* ── Fermer dropdown si click dehors ── */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('dama_current_user');
    setCurrentUser(null);
    setDropdownOpen(false);
    navigate('/');
  };

  const getInitials = (user) => {
    if (user.prenom && user.nom) {
      return `${user.prenom[0]}${user.nom[0]}`.toUpperCase();
    }
    return user.email[0].toUpperCase();
  };

  const getFirstName = (user) => {
    return user.prenom || user.email.split('@')[0];
  };

  return (
    <nav className="shared-navbar">
      {/* ── ROW 1 : Logo + Actions ── */}
      <div className="shared-navbar-inner">

        <Link to="/" className="shared-navbar-logo">
          <img src={logo} alt="DAMA Tunisia" className="shared-logo-img" />
          <div className="shared-logo-text-block">
            <div className="shared-logo-title-row">
              <span className="shared-logo-text">
                DAMA <span className="shared-logo-tn">Tunisia</span>
              </span>
              <span className="shared-logo-forming">(Forming Chapter)</span>
            </div>
            <span className="shared-logo-subtitle">Together, towards responsible DATA</span>
          </div>
        </Link>

        <div className="shared-navbar-actions">
          {currentUser ? (
            <div className="shared-user-pill-wrapper" ref={dropdownRef}>
              <button
                className="shared-user-pill"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <div className="shared-user-avatar">
                  {getInitials(currentUser)}
                </div>
                <span className="shared-user-name">{getFirstName(currentUser)}</span>
                <svg
                  className={`shared-chevron ${dropdownOpen ? 'open' : ''}`}
                  width="14" height="14" viewBox="0 0 14 14"
                  fill="none" xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {dropdownOpen && (
                <div className="shared-user-dropdown">
                  <div className="shared-dropdown-header">
                    <div className="shared-dropdown-avatar">
                      {getInitials(currentUser)}
                    </div>
                    <div>
                      <p className="shared-dropdown-name">
                        {currentUser.prenom} {currentUser.nom}
                      </p>
                      <p className="shared-dropdown-email">{currentUser.email}</p>
                    </div>
                  </div>
                  <div className="shared-dropdown-divider" />
                  <button className="shared-dropdown-logout" onClick={handleLogout}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Déconnexion
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" state={{ mode: 'login' }} className="shared-btn-login">LOGIN</Link>
              <Link to="/login" state={{ mode: 'subscribe' }} className="shared-btn-join">Subscribe to our Newsletter</Link>
            </>
          )}

          <a href="https://www.linkedin.com/company/dama-tunisia/posts/?feedView=all" target="_blank" rel="noreferrer" className="shared-btn-linkedin">in</a>
          <a href="https://www.facebook.com/people/DAMA-Tunisia-Chapter/61575921963330/" target="_blank" rel="noreferrer" className="shared-btn-facebook">f</a>
        </div>

      </div>

      {/* ── ROW 2 : Nav links (full-width, scrollable on mobile) ── */}
      <div className="shared-navbar-links-row">
        <div className="shared-navbar-links" ref={navLinksRef}>
          <Link to="/news"           className={isActive('/news')           ? 'active' : ''}>News</Link>
          <Link to="/events"         className={isActive('/events')         ? 'active' : ''}>Events</Link>
          <Link to="/learning"       className={isActive('/learning')       ? 'active' : ''}>Learning</Link>
          <Link to="/insights"       className={isActive('/insights')       ? 'active' : ''}>Insights</Link>
          <Link to="/chapters-board" className={isActive('/chapters-board') ? 'active' : ''}>Chapters Board</Link>
          <Link to="/about"          className={isActive('/about')          ? 'active' : ''}>About Us</Link>
          <Link to="/membership"     className={isActive('/membership')     ? 'active' : ''}>Become a Member</Link>
        </div>
      </div>

    </nav>
  );
};

export default SharedNavbar;